import './documentation.scss';

const sections = [
    ['headings', 'Headings'],
    ['formatting', 'Text formatting'],
    ['audio', 'Audio'],
    ['colors', 'Colors'],
    ['tables', 'Tables'],
    ['writing', 'Writing exercises'],
];

const Example = ({ source, children, label }) => (
    <div className="doc__example">
        <div className="doc__source">
            <div className="doc__column-label">Markup{label && <span>{label}</span>}</div>
            <pre><code>{source}</code></pre>
        </div>
        <div className="doc__preview">
            <div className="doc__column-label">Preview</div>
            <div className="doc__preview-content">{children}</div>
        </div>
    </div>
);

const Section = ({ id, title, description, children }) => (
    <section className="doc__section" id={id} aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`}>{title}</h2>
        <p className="doc__description">{description}</p>
        <div className="doc__examples">{children}</div>
    </section>
);

export default function MarkupDocumentation() {
    return (
        <main className="doc">
            <aside className="doc__sidebar">
                <nav aria-label="Documentation contents">
                    <p className="doc__eyebrow">Markup guide</p>
                    <a className="doc__overview" href="#overview">Overview</a>
                    <p className="doc__nav-label">On this page</p>
                    <ul>
                        {sections.map(([id, title]) => <li key={id}><a href={`#${id}`}>{title}</a></li>)}
                    </ul>
                </nav>
            </aside>

            <div className="doc__content">
                <header className="doc__header" id="overview">
                    <span className="doc__eyebrow">Creator resources</span>
                    <h1>Markup documentation</h1>
                    <p>Create clear, engaging lessons with Openlexia markup. Find the syntax you need and see how it appears in your course.</p>
                    <div className="doc__intro-note">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10v2" /></svg>
                        <p>Write the markup in your lesson editor. Each example pairs the source with its rendered result.</p>
                    </div>
                </header>

                <Section id="headings" title="Headings" description="Organize your lesson with three levels of headings. Add a space after the hash symbols.">
                    <Example source="# Header 1"><div className="doc__sample-h1">Header 1</div></Example>
                    <Example source="## Header 2"><div className="doc__sample-h2">Header 2</div></Example>
                    <Example source="### Header 3"><div className="doc__sample-h3">Header 3</div></Example>
                </Section>

                <Section id="formatting" title="Text formatting" description="Emphasize key words and phrases with bold or italic text.">
                    <Example source="**bold**" label="Bold"><strong>bold</strong></Example>
                    <Example source="*italic*" label="Italic"><em>italic</em></Example>
                </Section>

                <Section id="audio" title="Audio" description="Add an inline audio player using the ID of an audio file uploaded to your lesson.">
                    <Example source="inline audio: { AUDIO | audioId}">
                        <span className="doc__audio-example">inline audio: <span className="doc__audio-icon" role="img" aria-label="Audio player preview"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m9 5 10 7-10 7V5Z" /></svg></span></span>
                    </Example>
                    <p className="doc__hint">Replace <code>audioId</code> with your uploaded file’s ID. The player above is a visual preview.</p>
                </Section>

                <Section id="colors" title="Colors" description="Draw attention to a word or phrase with one of the supported text colors.">
                    <Example source="{ RED | color red}"><span className="color-red">color red</span></Example>
                    <Example source="{ BLUE | color blue}"><span className="color-blue">color blue</span></Example>
                    <Example source="{ GREEN | color green}"><span className="color-green">color green</span></Example>
                </Section>

                <Section id="tables" title="Tables" description="Start a table with @table and separate cells with vertical bars. Add a separator after the first row to create a header.">
                    <Example label="With header" source={'@table\n| Header 1 | Header 2 |\n|--------------------|\n| content  | content  |\n| content  | content  |\n| content  | content  |'}>
                        <table className="doc__table">
                            <thead><tr><th scope="col">Header 1</th><th scope="col">Header 2</th></tr></thead>
                            <tbody>{[1, 2, 3].map(row => <tr key={row}><td>content</td><td>content</td></tr>)}</tbody>
                        </table>
                    </Example>
                    <Example label="Without header" source={'@table\n| content | content |\n| content | content |'}>
                        <table className="doc__table"><tbody>{[1, 2].map(row => <tr key={row}><td>content</td><td>content</td></tr>)}</tbody></table>
                    </Example>
                </Section>

                <Section id="writing" title="Writing exercises" description="Start an exercise with @write. Wrap each answer in three dashes to turn it into a blank for the learner to fill in.">
                    <Example source={'@write\nLe chat ---se--- nettoie\n---Ce--- matin il fait chaud'}>
                        <div className="doc__writing-example">
                            <p>Le chat <span className="doc__answer" role="img" aria-label="Answer blank">…</span> nettoie</p>
                            <p><span className="doc__answer" role="img" aria-label="Answer blank">…</span> matin il fait chaud</p>
                        </div>
                    </Example>
                    <p className="doc__hint">The text between the dashes is the expected answer. Blanks are shown here as a visual preview.</p>
                </Section>

                <footer className="doc__footer">Openlexia markup guide <a href="#overview">Back to top <span aria-hidden="true">↑</span></a></footer>
            </div>
        </main>
    );
}
